import { InfoIcon, ThumbsDownIcon, ThumbsUpIcon, WarningIcon, XCircleIcon } from '@phosphor-icons/react'
import { createFormHook, createFormHookContexts, useSelector } from '@tanstack/react-form'
import { match, P } from 'ts-pattern'
import { type FeedbackRating, type FeedbackScope } from '../../utils/api-client/api-schemas.ts'
import { DefaultButton } from '../buttons/default-button.tsx'
import { GhostButton } from '../buttons/ghost-button.tsx'
import { TooltipWithHover } from '../tooltip.tsx'
import * as styles from './feedback-form.css.ts'
import { FeedbackScopeSelector, type FeedbackScopeSelectorProps } from './feedback-scope-selector.tsx'

// #region Types

export type FeedbackFormValues = {
  rating: FeedbackRating | undefined
  text: string
  /** Where the feedback applies. Repository by default — the narrowest scope. */
  scope: FeedbackScope
}

export type FeedbackFormProps = {
  isSubmitting: boolean
  onSubmit: () => unknown
  onCancel: () => unknown
  /** What the scope selector describes; omitted while the repository's membership is unknown. */
  scopeContext?: FeedbackScopeContext
}

export type FeedbackScopeContext = Pick<FeedbackScopeSelectorProps, 'isProjectRefused'> & {
  repositoryName: string
  /** The displayed project. Its id travels with a project-scoped submission so a stale one is refused. */
  project?: { id: string; name: string; repositoryCount?: number }
}

export type FeedbackSubmissionScope = { scope?: FeedbackScope; projectId?: string }

// #endregion

// #region Form Hook

const { fieldContext, formContext } = createFormHookContexts()

const { useAppForm, useTypedAppFormContext } = createFormHook({
  fieldComponents: {},
  formComponents: {},
  fieldContext,
  formContext,
})

const FORM_DEFAULT_VALUES: FeedbackFormValues = {
  rating: undefined,
  text: '',
  // Always defaults to the repository, whether or not it is grouped, so regrouping never changes
  // what an untouched submission means.
  scope: 'repository',
}

export const useFeedbackForm = (defaultValueOverrides: Partial<FeedbackFormValues> = {}) =>
  useAppForm({
    defaultValues: {
      ...FORM_DEFAULT_VALUES,
      ...defaultValueOverrides,
    },
  })

const useFormContext = () => useTypedAppFormContext({ defaultValues: FORM_DEFAULT_VALUES })

// #endregion

// #region Helpers

const MAX_CHARS = 1750

const SAMPLE_FEEDBACK_BULLETS = [
  '"User DTO objects are supplied from trusted sources"',
  '"This use of MD5 can\'t be altered because partners rely on MD5 being in the contract"',
  '"The @Valid annotation can\'t be trusted to stop injection attacks"',
  '"User DTO objects are trusted, so this is a false positive"',
]

const SAMPLE_FEEDBACK_FOOTER = 'Feel free to include relevant code examples as needed'

type CharCounterState = 'under-limit' | 'at-limit' | 'over-limit'

/**
 * The scope a submission carries, derived from the same facts the control displays so the pressed
 * segment is always what is sent. Nothing is sent while the membership is unknown, which leaves the
 * server's own default — the repository, the narrowest. Project scope only ever travels with the
 * project that was on screen; without one it falls back to the repository, because a bare `project`
 * lets the server pick the repository's current project, which nobody saw.
 */
export const getSubmissionScope = ({
  scope,
  scopeContext,
}: {
  scope: FeedbackScope
  scopeContext: FeedbackScopeContext | undefined
}): FeedbackSubmissionScope =>
  match({ scope, scopeContext })
    .with({ scopeContext: undefined }, () => ({}))
    .with(
      { scope: 'project', scopeContext: { project: P.nonNullable, isProjectRefused: P.not(true) } },
      ({ scopeContext: { project } }) => ({ scope: 'project' as const, projectId: project.id })
    )
    .otherwise(() => ({ scope: 'repository' as const }))

const getCharCounterState = (textLength: number): CharCounterState =>
  match({ isOverLimit: textLength > MAX_CHARS, isAtLimit: textLength === MAX_CHARS })
    .with({ isOverLimit: true }, () => 'over-limit' as const)
    .with({ isAtLimit: true }, () => 'at-limit' as const)
    .otherwise(() => 'under-limit' as const)

// #endregion

// #region FeedbackForm

export const FeedbackForm: React.FC<FeedbackFormProps> = ({ isSubmitting, onSubmit, onCancel, scopeContext }) => {
  const form = useFormContext()
  const rating = useSelector(form.store, state => state.values.rating)
  const text = useSelector(form.store, state => state.values.text)
  const scope = useSelector(form.store, state => state.values.scope)

  const isSubmitDisabled =
    rating === undefined || (rating === 'negative' && text.trim() === '') || text.length > MAX_CHARS

  const counterState = getCharCounterState(text.length)

  const counterClassName = match(counterState)
    .with('over-limit', () => styles.charCounterRed)
    .with('at-limit', () => styles.charCounterWhite)
    .otherwise(() => styles.charCounter)

  return (
    <form className={styles.form} onSubmit={e => e.preventDefault()}>
      <div className={styles.ratingRow}>
        <button
          className={styles.ratingButton}
          aria-label="Rate thumbs up"
          aria-pressed={rating === 'positive'}
          onClick={() => form.setFieldValue('rating', 'positive')}
          type="button"
        >
          <ThumbsUpIcon size={24} weight={rating === 'positive' ? 'fill' : 'regular'} />
        </button>
        <button
          className={styles.ratingButton}
          aria-label="Rate thumbs down"
          aria-pressed={rating === 'negative'}
          onClick={() => form.setFieldValue('rating', 'negative')}
          type="button"
        >
          <ThumbsDownIcon size={24} weight={rating === 'negative' ? 'fill' : 'regular'} />
        </button>
      </div>

      <div className={styles.textareaWrapper}>
        <div className={styles.textareaLabel}>
          <TooltipWithHover
            trigger={
              <span className={styles.sampleFeedbackLink}>
                <InfoIcon size={12} weight="bold" className={styles.infoIcon} /> Sample feedback
              </span>
            }
            maxWidth={700}
            side="top"
          >
            <div className={styles.tooltipBody}>
              <ul className={styles.tooltipList}>
                {SAMPLE_FEEDBACK_BULLETS.map(line => (
                  <li key={line} className={styles.tooltipListItem}>
                    {line}
                  </li>
                ))}
              </ul>
              <p className={styles.tooltipFooter}>{SAMPLE_FEEDBACK_FOOTER}</p>
            </div>
          </TooltipWithHover>
        </div>
        <div className={counterState === 'over-limit' ? styles.textareaBoxError : styles.textareaBox}>
          <textarea
            className={styles.textarea}
            value={text}
            onChange={e => form.setFieldValue('text', e.target.value)}
            placeholder="Pixee learns and acts immediately, so providing as much detail as possible will directly result in improvement. What was particularly useful about this outcome? How accurate and comprehensive was it? Was there context it missed?"
            aria-label="Feedback text"
          />
        </div>
        <div className={styles.charCounterRow}>
          {counterState === 'at-limit' && (
            <span className={styles.charCounterWarning}>
              <WarningIcon size={12} className={styles.warningIconColor} />
              <span>Character limit reached</span>
            </span>
          )}
          {counterState === 'over-limit' && (
            <span className={styles.charCounterError}>
              <XCircleIcon size={12} />
              <span>Character limit exceeded</span>
            </span>
          )}
          <span className={counterClassName}>{text.length.toLocaleString()}/1,750 characters</span>
        </div>
      </div>

      <FeedbackScopeSelector
        value={getSubmissionScope({ scope, scopeContext }).scope ?? 'repository'}
        onChange={value => form.setFieldValue('scope', value)}
        repositoryName={scopeContext?.repositoryName}
        project={scopeContext?.project}
        isProjectRefused={scopeContext?.isProjectRefused}
      />

      <div className={styles.buttonGroup}>
        <GhostButton type="secondary" onClick={onCancel}>
          Cancel
        </GhostButton>
        <DefaultButton
          buttonType="primary"
          state={isSubmitting ? 'loading' : isSubmitDisabled ? 'disabled' : 'default'}
          onClick={onSubmit}
          data-testid="submit-feedback-button"
        >
          Submit feedback
        </DefaultButton>
      </div>
    </form>
  )
}

// #endregion
